import type { Json, SiteConfig } from "../types";

export interface WebVitalsTracker {
  destroy(): void;
  emitFinal(): void;
}

export interface WebVitalsOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getConfig: () => SiteConfig | null;
}

type Rating = "good" | "needs_improvement" | "poor";

function rate(value: number, good: number, ni: number): Rating {
  if (value <= good) return "good";
  if (value <= ni) return "needs_improvement";
  return "poor";
}

interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface EventTimingEntry extends PerformanceEntry {
  duration: number;
  interactionId?: number;
}

export function installWebVitals(opts: WebVitalsOptions): WebVitalsTracker {
  if (typeof PerformanceObserver !== "function") {
    return { destroy: () => undefined, emitFinal: () => undefined };
  }

  function shouldTrack(): boolean {
    const c = opts.getConfig();
    return !(c && c.tracking && c.tracking.web_vitals === false);
  }

  // LCP — keep the last reported value.
  let lcpValue = 0;
  let lcpObs: PerformanceObserver | null = null;
  try {
    lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      if (entries.length === 0) return;
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number; renderTime?: number };
      lcpValue = Math.round(last.renderTime || last.startTime || 0);
    });
    lcpObs.observe({ type: "largest-contentful-paint", buffered: true } as PerformanceObserverInit);
  } catch {
    lcpObs = null;
  }

  // CLS — session window aggregation.
  let clsValue = 0;
  let sessionValue = 0;
  let sessionStart = 0;
  let sessionLast = 0;
  let clsObs: PerformanceObserver | null = null;
  try {
    clsObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as LayoutShift[]) {
        if (entry.hadRecentInput) continue;
        if (sessionValue && entry.startTime - sessionLast < 1000 && entry.startTime - sessionStart < 5000) {
          sessionValue += entry.value;
          sessionLast = entry.startTime;
        } else {
          sessionValue = entry.value;
          sessionStart = entry.startTime;
          sessionLast = entry.startTime;
        }
        if (sessionValue > clsValue) clsValue = sessionValue;
      }
    });
    clsObs.observe({ type: "layout-shift", buffered: true } as PerformanceObserverInit);
  } catch {
    clsObs = null;
  }

  // INP — slowest interaction duration (very simplified vs. spec but acceptable for MVP).
  let inpValue = 0;
  let inpObs: PerformanceObserver | null = null;
  try {
    inpObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as EventTimingEntry[]) {
        if (!entry.interactionId) continue;
        if (entry.duration > inpValue) inpValue = Math.round(entry.duration);
      }
    });
    inpObs.observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
  } catch {
    inpObs = null;
  }

  let emitted = false;

  function emitFinal() {
    if (emitted || !shouldTrack()) return;
    emitted = true;
    if (lcpValue > 0) {
      opts.emit("web_vital_lcp", { value: lcpValue, rating: rate(lcpValue, 2500, 4000) });
    }
    if (inpValue > 0) {
      opts.emit("web_vital_inp", { value: inpValue, rating: rate(inpValue, 200, 500) });
    }
    // Emit CLS even when 0 — it's a useful signal that layout was stable.
    const clsRounded = Math.round(clsValue * 1000) / 1000;
    opts.emit("web_vital_cls", { value: clsRounded, rating: rate(clsRounded, 0.1, 0.25) });
  }

  function onHidden() {
    if (document.visibilityState === "hidden") emitFinal();
  }

  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", emitFinal);

  return {
    destroy(): void {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", emitFinal);
      lcpObs?.disconnect();
      clsObs?.disconnect();
      inpObs?.disconnect();
    },
    emitFinal,
  };
}
